import { Security } from "@neru/base/features";
import { useNotifications, useUserAccounts } from "@neru/base/hooks";
import { AuthServiceProvider, Emitter } from "@neru/base/services";
import { type AccountIdProp } from "@neru/base/types";
import { getLogger } from "@neru/base/utils";
import { AccessDenied } from "@neru/base/utils/errors";
import type { WithChildren } from "@neru/types";
import { Banner, Spinner } from "@neru/ui";
import { AuthProvider, useAuthService } from "pkce-hook";
import { useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";

import { config } from "../utils/config";

const authConfig = {
  ...config.authConfig,
  authorizeEndpoint: `${config.authConfig.provider}/auth`,
  autoRefresh: true,
  logoutEndpoint: `${config.authConfig.provider}/sessions/logout`,
  namespace: "vch",
  scopes: ["openid", "email", "offline_access"],
};

const log = getLogger("OpenIDConnect");

const WithUserAccounts = ({ children }: WithChildren) => {
  const authService = useAuthService();

  if (new AuthServiceProvider(authService)) {
    log.info("Auth service provider is initialized.");
  }

  const navigate = useNavigate();
  const { showError } = useNotifications();

  const { data, error, loading } = useUserAccounts();

  useLayoutEffect(() => {
    const navigateTo = localStorage.getItem("vch.navigate_to");
    const currentPathWithHash = window.location.pathname + window.location.hash;

    if (authService.isAuthenticated() && navigateTo) {
      localStorage.removeItem("vch.navigate_to");
      if (navigateTo !== currentPathWithHash) {
        navigate(navigateTo);
      }
    } else if (authService.authError) {
      Emitter.emit("error", new AccessDenied(authService.authError));
    }
  }, [authService, navigate, showError]);

  if (authService.isPending() || loading) {
    return <Spinner data-testid="auth-accounts-pending" />;
  }

  if (authService.isAuthenticated() && (error || !data?.length)) {
    return (
      <Banner
        connotation="alert"
        data-testid="alert-unauthorized-banner"
        message="Could not authorize user. Please contact support."
        open
      />
    );
  }

  return children;
};

const WithAuth = ({ accountId, children }: WithChildren & AccountIdProp) => (
  <AuthProvider config={authConfig}>
    <WithUserAccounts>
      <Security accountId={accountId} skipAuth={Boolean(config.skipAuth)}>
        {children}
      </Security>
    </WithUserAccounts>
  </AuthProvider>
);

export default WithAuth;
